/**
 * Loads Judge.me reviews into the Customer reviews section (Figma 1:158).
 * Uses the reviews JSON API for reliable sorting; falls back to the featured
 * carousel widget when the API is unavailable.
 */
(function () {
  const STAR_PATH =
    'M8 1.2l1.76 3.57 3.94.57-2.85 2.78.67 3.92L8 10.9l-3.52 1.85.67-3.92L2.3 5.34l3.94-.57L8 1.2z';

  const namespace = (window.MatchaCustomerReviews = window.MatchaCustomerReviews || {});
  namespace.controllers = namespace.controllers || new WeakMap();

  function initSection(root) {
    const token = root.dataset.apiToken?.trim();
    const shopDomain = root.dataset.shopDomain?.trim();
    const sectionId = root.dataset.sectionId;
    const count = parseInt(root.dataset.reviewCount || '3', 10);
    const textColor = root.dataset.textColor || '#1a3827';

    if (!token || !shopDomain) return;

    const grid = root.querySelector(`#CustomerReviewsGrid-${sectionId}`);
    if (!grid) return;

    namespace.controllers.get(root)?.abort();

    const controller = new AbortController();
    namespace.controllers.set(root, controller);
    const { signal } = controller;

    fetchReviews(token, shopDomain, count, signal)
      .then((reviews) => {
        if (signal.aborted) return;
        if (!reviews.length) return;

        grid.innerHTML = '';
        reviews.forEach((review) => {
          grid.appendChild(buildCard(review, sectionId, textColor));
        });
      })
      .catch((err) => {
        if (signal.aborted || err?.name === 'AbortError') return;
        console.warn('[customer-reviews] Could not load Judge.me reviews:', err);
      });
  }

  async function fetchReviews(token, shopDomain, count, signal) {
    try {
      const reviews = await fetchReviewsApi(token, shopDomain, signal);
      if (signal.aborted) return [];
      if (reviews.length) return reviews.slice(0, count);
    } catch (apiError) {
      if (signal.aborted || apiError?.name === 'AbortError') return [];
      console.warn('[customer-reviews] Reviews API unavailable, trying carousel:', apiError);
    }

    const carouselReviews = await fetchCarouselReviews(token, shopDomain, signal);
    if (signal.aborted) return [];
    return carouselReviews.slice(0, count);
  }

  async function fetchReviewsApi(token, shopDomain, signal) {
    const url = new URL('https://judge.me/api/v1/reviews');
    url.searchParams.set('api_token', token);
    url.searchParams.set('shop_domain', shopDomain);
    url.searchParams.set('per_page', '100');

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Judge.me reviews API ${response.status}`);

    const data = await response.json();
    const raw = Array.isArray(data.reviews) ? data.reviews : [];

    return sortReviews(
      raw
        .filter((entry) => !entry.hidden)
        .map((entry) => ({
          id: String(entry.id || entry.reviewer?.name || Math.random()),
          rating: Number(entry.rating) || 0,
          name: entry.reviewer?.name?.trim() || 'Anonymous',
          body: entry.body?.trim() || entry.title?.trim() || '',
          date: entry.created_at || entry.updated_at || '',
        }))
        .filter((review) => review.body)
    );
  }

  async function fetchCarouselReviews(token, shopDomain, signal) {
    const url = new URL('https://judge.me/api/v1/widgets/featured_carousel');
    url.searchParams.set('api_token', token);
    url.searchParams.set('shop_domain', shopDomain);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Judge.me carousel API ${response.status}`);

    const data = await response.json();
    const widgetHtml = data.featured_carousel || data.widget;
    if (!widgetHtml) throw new Error('Judge.me carousel response empty');

    return sortReviews(parseCarouselWidget(widgetHtml));
  }

  function parseCarouselWidget(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelectorAll('.jdgm-carousel-item');

    return Array.from(items)
      .map((el) => {
        const ratingEl = el.querySelector('.jdgm-carousel-item__review-rating');
        let rating = 0;

        if (ratingEl) {
          const aria = ratingEl.getAttribute('aria-label') || '';
          const match = aria.match(/(\d+)/);
          rating = match
            ? parseInt(match[1], 10)
            : ratingEl.querySelectorAll('.jdgm-star.jdgm--on').length;
        }

        const name =
          el.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent?.trim() ||
          'Anonymous';
        const body =
          el.querySelector('.jdgm-carousel-item__review-body p')?.textContent?.trim() ||
          el.querySelector('.jdgm-carousel-item__review-body')?.textContent?.trim() ||
          '';
        const title =
          el.querySelector('.jdgm-carousel-item__review-title')?.textContent?.trim() || '';
        const timestampEl = el.querySelector('.jdgm-carousel-item__timestamp');
        const date =
          timestampEl?.getAttribute('data-time') ||
          timestampEl?.getAttribute('datetime') ||
          timestampEl?.textContent?.trim() ||
          '';

        return {
          id: el.getAttribute('data-review-id') || name,
          rating,
          name,
          body: body || title,
          date,
        };
      })
      .filter((review) => review.body);
  }

  function sortReviews(reviews) {
    const sorted = [...reviews];
    sorted.sort((a, b) => b.rating - a.rating || parseDate(b.date) - parseDate(a.date));
    return sorted;
  }

  function parseDate(value) {
    if (!value) return 0;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function buildCard(review, sectionId, textColor) {
    const article = document.createElement('article');
    article.className = 'customer-reviews__card';

    const header = document.createElement('div');
    header.className = 'customer-reviews__header';

    header.appendChild(buildAvatar(review));
    header.appendChild(buildMeta(review, sectionId, textColor));
    article.appendChild(header);

    const body = document.createElement('p');
    body.className = 'customer-reviews__body';
    body.textContent = review.body;
    article.appendChild(body);

    return article;
  }

  function buildMeta(review, sectionId, textColor) {
    const meta = document.createElement('div');
    meta.className = 'customer-reviews__meta';

    const name = document.createElement('p');
    name.className = 'customer-reviews__name';
    name.textContent = review.name;
    meta.appendChild(name);
    meta.appendChild(buildStars(review.rating || 0, sectionId, review.id, textColor));

    return meta;
  }

  function buildAvatar(review) {
    const name = review.name || 'A';
    const initial = name.trim().charAt(0).toUpperCase() || 'A';

    const span = document.createElement('span');
    span.className = 'customer-reviews__avatar customer-reviews__avatar--initials';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = initial;
    return span;
  }

  function buildStars(rating, sectionId, reviewId, textColor) {
    const wrap = document.createElement('div');
    wrap.className = 'customer-reviews__stars';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', `${rating} out of 5 stars`);

    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.25 && rating % 1 < 0.75;
    const roundUp = rating % 1 >= 0.75;

    for (let i = 1; i <= 5; i++) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'customer-reviews__star');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('aria-hidden', 'true');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', STAR_PATH);

      if (i <= fullStars || (roundUp && i === fullStars + 1)) {
        path.setAttribute('fill', textColor);
      } else if (hasHalf && i === fullStars + 1) {
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
        grad.id = `half-${sectionId}-${reviewId}-${i}`;
        const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        stop1.setAttribute('offset', '50%');
        stop1.setAttribute('stop-color', textColor);
        const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        stop2.setAttribute('offset', '50%');
        stop2.setAttribute('stop-color', 'transparent');
        grad.appendChild(stop1);
        grad.appendChild(stop2);
        defs.appendChild(grad);
        svg.appendChild(defs);
        path.setAttribute('fill', `url(#half-${sectionId}-${reviewId}-${i})`);
        path.setAttribute('stroke', textColor);
        path.setAttribute('stroke-width', '0.5');
      } else {
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', textColor);
        path.setAttribute('stroke-width', '1');
      }

      svg.appendChild(path);
      wrap.appendChild(svg);
    }

    return wrap;
  }

  function boot() {
    document.querySelectorAll('[data-customer-reviews]').forEach(initSection);
  }

  namespace.initSection = initSection;
  namespace.boot = boot;

  if (!namespace.bound) {
    namespace.bound = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    document.addEventListener('shopify:section:load', (event) => {
      const root = event.target?.querySelector?.('[data-customer-reviews]');
      if (root) initSection(root);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target?.querySelector?.('[data-customer-reviews]');
      namespace.controllers.get(root)?.abort();
    });
  } else if (document.readyState !== 'loading') {
    boot();
  }
})();
